import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, RotateCw, Sparkles, Repeat, Info } from 'lucide-react';
import { useLernsets } from './LearnsetContext';
import { useAuth } from '../auth/AuthContext';
import { Card } from '../util/types';

type SortMode = 'date' | 'random' | 'smart';

// Eigene Kartenkomponente mit internem Flip-Zustand
const CardComponent = ({ card, swapQA }: { card: Card; swapQA: boolean }) => {
    const [flipped, setFlipped] = useState(false);
    const handleFlip = () => setFlipped(!flipped);
    const showQuestionFirst = !swapQA;
    const frontText = showQuestionFirst ? card.question : card.answer;
    const backText = showQuestionFirst ? card.answer : card.question;
    const frontLabel = showQuestionFirst ? 'Frage' : 'Antwort';
    const backLabel = showQuestionFirst ? 'Antwort' : 'Frage';

    return (
        <div className="relative w-full cursor-pointer group" onClick={handleFlip} style={{ perspective: '1000px' }}>
            <motion.div
                className="relative w-full"
                animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                style={{ transformStyle: 'preserve-3d', minHeight: '360px' }}
            >
                {/* Vorderseite */}
                <motion.div
                    className="absolute inset-0 bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl border border-white/50 p-8 flex flex-col group-hover:shadow-3xl transition-shadow duration-300"
                    style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', overflowY: 'auto' }}
                >
                    <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">{frontLabel}</h3>
                    <p className="text-2xl text-gray-800 flex-1 leading-relaxed whitespace-pre-wrap">{frontText}</p>
                    {!flipped && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="flex justify-center items-center gap-2 text-xs text-gray-400 mt-6"
                        >
                            <Info className="w-3 h-3" />
                            Zum Umdrehen klicken
                        </motion.div>
                    )}
                </motion.div>
                {/* Rückseite */}
                <motion.div
                    className="absolute inset-0 bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl border border-white/50 p-8 flex flex-col"
                    style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', overflowY: 'auto' }}
                >
                    <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">{backLabel}</h3>
                    <p className="text-2xl text-gray-800 flex-1 leading-relaxed whitespace-pre-wrap">{backText}</p>
                </motion.div>
            </motion.div>
        </div>
    );
};

const LearnMode = () => {
    const { id, mode } = useParams<{ id: string; mode: string }>();
    const { getLernsetById, updateLernset } = useLernsets();
    const lernset = getLernsetById(id!);
    const { profile } = useAuth();
    const isPro = profile?.tier === 'pro';

    const [sortMode, setSortMode] = useState<SortMode>('date');
    const [swapQA, setSwapQA] = useState(false);
    const [sessionCards, setSessionCards] = useState<Card[]>([]);
    const [sessionRatings, setSessionRatings] = useState<Map<string, 'good' | 'medium' | 'bad'>>(new Map());
    const [currentIndex, setCurrentIndex] = useState(0);
    const [animationDirection, setAnimationDirection] = useState<'left' | 'right' | 'up' | null>(null);
    const [loading, setLoading] = useState(true);

    const initialised = useRef(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const sortSmart = (cards: Card[]): Card[] => {
        return [...cards].sort((a, b) => {
            if (a.strength !== b.strength) return a.strength - b.strength;
            return a.lastReviewedAt - b.lastReviewedAt;
        });
    };

    const buildSession = useCallback(async () => {
        if (!lernset) return;
        setLoading(true);

        let cards: Card[] = [];
        if (mode === 'daily') {
            const now = Date.now();
            cards = lernset.cards.filter(c => c.nextDueAt <= now);
        } else {
            cards = [...lernset.cards];
        }

        if (cards.length === 0) {
            setSessionCards([]);
            setLoading(false);
            return;
        }

        let sorted: Card[] = [...cards];
        switch (sortMode) {
            case 'date':
                sorted.sort((a, b) => a.createdAt - b.createdAt);
                break;
            case 'random':
                sorted = sorted.sort(() => Math.random() - 0.5);
                break;
            case 'smart':
                if (isPro) sorted = sortSmart(sorted);
                else sorted.sort((a, b) => a.createdAt - b.createdAt);
                break;
        }

        const initialRatings = new Map<string, 'good' | 'medium' | 'bad'>();
        sorted.forEach(card => {
            if (card.lastRating) initialRatings.set(card.id, card.lastRating);
        });
        setSessionRatings(initialRatings);
        setSessionCards(sorted);
        setCurrentIndex(0);
        setLoading(false);
    }, [lernset, mode, sortMode, isPro]);

    useEffect(() => {
        if (!lernset) return;
        if (!initialised.current) {
            buildSession();
            initialised.current = true;
        } else {
            buildSession();
        }
    }, [mode, sortMode]);

    const handleNext = async (rating: 'bad' | 'medium' | 'good') => {
        if (sessionCards.length === 0) return;

        setAnimationDirection('right');
        setTimeout(() => setAnimationDirection(null), 100);

        const currentCard = sessionCards[currentIndex];
        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;

        let strengthChange = 0;
        let nextDueAt = 0;
        switch (rating) {
            case 'bad':
                strengthChange = -25;
                nextDueAt = now + oneDay;
                break;
            case 'medium':
                strengthChange = -5;
                nextDueAt = now + 2 * oneDay;
                break;
            case 'good':
                strengthChange = 15;
                nextDueAt = now + 5 * oneDay;
                break;
        }
        const newStrength = Math.max(0, Math.min(100, currentCard.strength + strengthChange));
        const updatedCard: Card = {
            ...currentCard,
            strength: newStrength,
            nextDueAt,
            lastReviewedAt: now,
            reviewCount: currentCard.reviewCount + 1,
            lastRating: rating,
        };

        const updatedSessionCards = sessionCards.map(c =>
            c.id === currentCard.id ? updatedCard : c
        );

        let newCards = [...updatedSessionCards];
        const [card] = newCards.splice(currentIndex, 1);
        newCards.push(card);
        let newIndex = currentIndex >= newCards.length ? 0 : currentIndex;

        setSessionRatings(prev => {
            const newMap = new Map(prev);
            newMap.set(currentCard.id, rating);
            return newMap;
        });

        setSessionCards(newCards);
        setCurrentIndex(newIndex);

        try {
            await updateLernset(id!, { cards: newCards });
        } catch (error) {
            console.error('Fehler beim Speichern der Bewertung:', error);
        }
    };

    const handleRestart = () => {
        if (!lernset) return;
        buildSession();
    };

    const totalSessionCards = sessionCards.length;
    const goodCount = Array.from(sessionRatings.values()).filter(r => r === 'good').length;
    const mediumCount = Array.from(sessionRatings.values()).filter(r => r === 'medium').length;
    const badCount = Array.from(sessionRatings.values()).filter(r => r === 'bad').length;
    const goodPercent = totalSessionCards > 0 ? (goodCount / totalSessionCards) * 100 : 0;
    const mediumPercent = totalSessionCards > 0 ? (mediumCount / totalSessionCards) * 100 : 0;
    const badPercent = totalSessionCards > 0 ? (badCount / totalSessionCards) * 100 : 0;

    const currentCard = sessionCards[currentIndex];

    if (!lernset) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Lernset nicht gefunden</h1>
                <Link to="/lernsets" className="text-indigo-600 hover:underline">
                    Zurück zu meinen Lernsets
                </Link>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
            </div>
        );
    }

    if (sessionCards.length === 0) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 text-center"
            >
                {mode === 'daily' ? (
                    <>
                        <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Keine fälligen Karten
                        </h1>
                        <p className="text-gray-600 mb-6">
                            Super! Du hast heute alle Karten gelernt. Komm morgen wieder vorbei.
                        </p>
                    </>
                ) : (
                    <>
                        <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Keine Karteikarten
                        </h1>
                        <p className="text-gray-600 mb-6">
                            Dieses Lernset enthält noch keine Karten.
                        </p>
                    </>
                )}
                <Link
                    to={`/lernset/${id}`}
                    className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:bg-indigo-700"
                >
                    Zum Dashboard
                </Link>
            </motion.div>
        );
    }

    const showQuestionFirst = !swapQA;
    const frontText = showQuestionFirst ? currentCard.question : currentCard.answer;
    const backText = showQuestionFirst ? currentCard.answer : currentCard.question;
    const frontLabel = showQuestionFirst ? 'Frage' : 'Antwort';
    const backLabel = showQuestionFirst ? 'Antwort' : 'Frage';

    const cardVariants = {
        initial: (direction: 'left' | 'right' | 'up' | null) => {
            if (direction === 'left') return { x: -200, rotate: -15, opacity: 0, scale: 0.9 };
            if (direction === 'right') return { x: 200, rotate: 15, opacity: 0, scale: 0.9 };
            if (direction === 'up') return { y: -200, rotate: 5, opacity: 0, scale: 0.9 };
            return { x: 0, rotate: 0, opacity: 0, scale: 0.9 };
        },
        animate: {
            x: 0,
            y: 0,
            rotate: 0,
            opacity: 1,
            scale: 1,
            transition: { duration: 0.3, ease: 'easeOut' } as const,
        },
        exit: (direction: 'left' | 'right' | 'up' | null) => {
            if (direction === 'left') return { x: 200, rotate: 15, opacity: 0, scale: 0.9 };
            if (direction === 'right') return { x: -200, rotate: -15, opacity: 0, scale: 0.9 };
            if (direction === 'up') return { y: 200, rotate: -5, opacity: 0, scale: 0.9 };
            return { x: 0, rotate: 0, opacity: 0, scale: 0.9 };
        },
    };

    return (
        <div className="relative min-h-screen overflow-hidden">
            <motion.div
                className="absolute inset-0 -z-10"
                animate={{
                    background: [
                        'linear-gradient(135deg, #f0f9ff 0%, #e6f0fa 50%, #f5f3ff 100%)',
                        'linear-gradient(225deg, #f0f9ff 0%, #e6f0fa 50%, #f5f3ff 100%)',
                        'linear-gradient(135deg, #f0f9ff 0%, #e6f0fa 50%, #f5f3ff 100%)',
                    ],
                }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            />

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="relative p-4 pt-24"
            >
                <div className="max-w-3xl mx-auto">
                    <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="flex items-center justify-between mb-4"
                    >
                        <Link to={`/lernset/${id}`} className="text-gray-600 hover:text-indigo-600 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div className="flex items-center gap-3">
                            <select
                                value={sortMode}
                                onChange={(e) => setSortMode(e.target.value as SortMode)}
                                className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer"
                            >
                                <option value="date">Nach Datum</option>
                                <option value="random">Zufällig</option>
                                <option value="smart" disabled={!isPro}>
                                    Smart {!isPro && "(nur Pro)"}
                                </option>
                            </select>
                            <button
                                onClick={() => setSwapQA(!swapQA)}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    swapQA
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : 'bg-white/80 backdrop-blur-sm text-gray-600 hover:bg-indigo-50'
                                }`}
                            >
                                <Repeat className="w-4 h-4" />
                                {swapQA ? 'Antwort zuerst' : 'Frage zuerst'}
                            </button>
                        </div>
                        <div className="text-sm text-gray-500 bg-white/50 backdrop-blur-sm px-3 py-1 rounded-full">
                            {currentIndex + 1} / {sessionCards.length}
                        </div>
                    </motion.div>

                    <div className="w-full h-3 bg-gray-200/70 rounded-full overflow-hidden mb-6">
                        <div className="h-full flex">
                            <motion.div
                                className="h-full bg-green-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${goodPercent}%` }}
                                transition={{ type: 'spring', stiffness: 100, damping: 20 }}
                            />
                            <motion.div
                                className="h-full bg-yellow-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${mediumPercent}%` }}
                                transition={{ type: 'spring', stiffness: 100, damping: 20, delay: 0.05 }}
                            />
                            <motion.div
                                className="h-full bg-red-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${badPercent}%` }}
                                transition={{ type: 'spring', stiffness: 100, damping: 20, delay: 0.1 }}
                            />
                        </div>
                    </div>

                    <AnimatePresence mode="wait" custom={animationDirection}>
                        <motion.div
                            key={currentCard.id}
                            custom={animationDirection}
                            variants={cardVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                        >
                            <CardComponent card={currentCard} swapQA={swapQA} />
                        </motion.div>
                    </AnimatePresence>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="flex gap-3 justify-center mt-8"
                    >
                        <button
                            onClick={() => handleNext('bad')}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold transition-all bg-red-500 text-white hover:bg-red-600 hover:scale-105 active:scale-95 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                            😞 Schlecht
                        </button>
                        <button
                            onClick={() => handleNext('medium')}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold transition-all bg-yellow-500 text-white hover:bg-yellow-600 hover:scale-105 active:scale-95 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                            😐 Mittel
                        </button>
                        <button
                            onClick={() => handleNext('good')}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold transition-all bg-green-500 text-white hover:bg-green-600 hover:scale-105 active:scale-95 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                            😊 Gut
                        </button>
                    </motion.div>
                    <p className="text-xs text-gray-400 text-center mt-4">
                        Klicke auf die Buttons, um zu bewerten – die Karte wird entsprechend eingeordnet
                    </p>

                    {/* Neustart-Button NUR im täglichen Modus anzeigen */}
                    {mode === 'daily' && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="flex justify-center mt-4"
                        >
                            <button
                                onClick={handleRestart}
                                className="px-4 py-2 text-sm text-gray-500 hover:text-indigo-600 flex items-center gap-1 transition-colors bg-white/40 backdrop-blur-sm rounded-full"
                            >
                                <RotateCw className="w-4 h-4" />
                                Neustart (Bewertungen zurücksetzen)
                            </button>
                        </motion.div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default LearnMode;